// The plugin manifest: the declarative contract a plugin ships in manifest.json.
// Pure types — no runtime. See spec §5.3.

/** Native capabilities a plugin can request. All are brokered and consented. */
export type PermissionCapability = 'fs.read' | 'fs.write' | 'pty' | 'net' | 'clipboard' | 'git';

/**
 * One requested capability. `reason` is required (the validator rejects an empty
 * one) so the consent UI can always explain why. `optional` defaults to false
 * (mandatory). `scope` narrows fs grants (e.g. 'workspace'); `domains` narrows
 * net grants to an allowlist.
 */
export interface PermissionRequest {
  capability: PermissionCapability;
  reason: string;
  optional?: boolean;
  scope?: string;
  domains?: string[];
}

export interface ContributedLanguage {
  id: string;
  extensions: string[];
  aliases?: string[];
  filenames?: string[];
}

export interface ContributedGrammar {
  language: string;
  cmLanguage?: string;
  shikiGrammar?: string;
  scopeName?: string;
}

export interface ContributedTheme {
  id: string;
  label: string;
  type: 'light' | 'dark';
  path: string;
}

export interface ContributedOutlineProvider {
  language: string;
}

export interface ContributedFoldProvider {
  language: string;
}

export interface ContributedEmbedRenderer {
  language: string;
  surface?: string;
}

export interface ContributedCommand {
  id: string;
  title: string;
  category?: string;
  icon?: string;
  keybinding?: string;
}

export interface ContributedActivityBarItem {
  id: string;
  title: string;
  icon: string;
  view: string;
}

export interface ContributedView {
  id: string;
  container: string;
  title: string;
  kind: 'tree' | 'surface';
}

/** A full editor-area "main view" contributed by a plugin (rendered in place of the document editor). */
export interface ContributedMainView {
  id: string;
  title: string;
  icon?: string;
}

export interface ContributedTopBarAction {
  id: string;
  icon: string;
  title: string;
  command: string;
  placement: 'beforeHelp';
}

export interface ContributedToolbarItem {
  id: string;
  command: string;
  icon?: string;
  title?: string;
}

export interface ContributedStatusBarItem {
  id: string;
  alignment: 'left' | 'right';
  command: string;
  icon?: string;
  title?: string;
}

export interface ContributedMenuItem {
  command: string;
  when?: string;
}

export interface ContributedMenus {
  logo?: ContributedMenuItem[];
  fileTreeContext?: ContributedMenuItem[];
  editorContext?: ContributedMenuItem[];
}

export interface ContributedTool {
  id: string;
  title: string;
  icon: string;
}

export interface ContributedSetting {
  key: string;
  type: 'string' | 'number' | 'boolean';
  default: unknown;
  title: string;
  description?: string;
}

export interface ContributedSurface {
  id: string;
  path: string;
  type: 'panel' | 'modal';
}

/** Every declarative contribution point. The validator rejects unknown keys. */
export interface Contributions {
  languages?: ContributedLanguage[];
  grammars?: ContributedGrammar[];
  themes?: ContributedTheme[];
  outlineProviders?: ContributedOutlineProvider[];
  foldProviders?: ContributedFoldProvider[];
  embedRenderers?: ContributedEmbedRenderer[];
  commands?: ContributedCommand[];
  activityBar?: ContributedActivityBarItem[];
  views?: ContributedView[];
  /** Full editor-area views, rendered in place of the document editor. */
  mainViews?: ContributedMainView[];
  topBarActions?: ContributedTopBarAction[];
  toolbar?: ContributedToolbarItem[];
  statusBar?: ContributedStatusBarItem[];
  menus?: ContributedMenus;
  tools?: ContributedTool[];
  settings?: ContributedSetting[];
  surfaces?: ContributedSurface[];
}

export interface MarketplaceMeta {
  categories?: string[];
  keywords?: string[];
  screenshots?: string[];
}

/** The top-level plugin manifest. */
export interface PluginManifest {
  id: string;
  name: string;
  version: string;
  publisher: string;
  description?: string;
  icon?: string;
  readme?: string;
  engines: { slate: string };
  main?: string;
  activationEvents?: string[];
  /**
   * Short slash prefix for this plugin's palette commands, e.g. 'wc' so its
   * commands invoke as `/wc:count`. Defaults to the last dot-segment of `id`
   * (e.g. 'slate.hello' -> 'hello' -> `/hello:greet`).
   */
  commandPrefix?: string;
  permissions?: PermissionRequest[];
  contributes?: Contributions;
  marketplace?: MarketplaceMeta;
}

/** The set of valid `contributes` keys, used by the validator. */
export const CONTRIBUTION_KEYS: readonly (keyof Contributions)[] = [
  'languages',
  'grammars',
  'themes',
  'outlineProviders',
  'foldProviders',
  'embedRenderers',
  'commands',
  'activityBar',
  'views',
  'mainViews',
  'topBarActions',
  'toolbar',
  'statusBar',
  'menus',
  'tools',
  'settings',
  'surfaces',
];

/** The set of valid permission capabilities, used by the validator. */
export const PERMISSION_CAPABILITIES: readonly PermissionCapability[] = [
  'fs.read',
  'fs.write',
  'pty',
  'net',
  'clipboard',
  'git',
];
